import {isFeatureEnabled} from '@github-ui/feature-flags'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useAnalytics} from '@github-ui/use-analytics'
import {useRef} from 'react'

import {DisplayMode} from '@github-ui/shared-dashboard-lists/shared/types/list-types'

import {COMPACT_MODE_COOKIE_NAME} from '../constants/cookies'
import {setPreferenceCookie} from '../helpers/preference-cookies'
import {persistPullsPreferences} from '../helpers/pulls-preferences-api'

/**
 * Hook to manage and persist the compact mode setting for lists.
 * @param compactMode The initial compact mode value.
 * @param preferenceKey The key to use for the FormData on the legacy endpoint.
 * @param options.updatesEnabled When false, updates are ignored so callers can
 *   safely deploy persistence changes behind a feature flag.
 * @param options.persistAsPullsPreference When true, the value is also written
 *   to the shared dashboard-surface cookie and, behind the
 *   persistent-preferences flag, persisted to the dashboard surface preferences
 *   table instead of the legacy UserSettings endpoint.
 */
export function useUpdateCompactModeSetting(
  compactMode: boolean,
  preferenceKey: string,
  options?: {updatesEnabled?: boolean; persistAsPullsPreference?: boolean},
) {
  const savedCompactModeRef = useRef(compactMode)

  const {sendAnalyticsEvent} = useAnalytics()

  async function updateCompactModeSetting(value: boolean) {
    if (options?.updatesEnabled === false || savedCompactModeRef.current === value) {
      return
    }

    sendAnalyticsEvent('pull-requests.click', 'DENSITY_TOGGLE', {
      density: value ? DisplayMode.Compact : DisplayMode.Default,
    })

    savedCompactModeRef.current = value

    if (options?.persistAsPullsPreference) {
      setPreferenceCookie(COMPACT_MODE_COOKIE_NAME, value.toString())

      if (isFeatureEnabled('dashboard_surface_persistent_preferences')) {
        persistPullsPreferences({compact_mode: value})
        return
      }
    }

    const formData = new FormData()
    formData.set(preferenceKey, value.toString())
    await verifiedFetch('/pulls/preferences', {method: 'PUT', body: formData})
  }

  return {updateCompactModeSetting}
}
